import React, { useState, useCallback, useMemo } from "react";
import { ApolloError } from "@apollo/client";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Breadcrumb, Table, message } from "antd";
import { Properties, Users } from "graphql/generated/graphql";

import routeNames from "constants/route-names";
import {
	useGetOrganizationByIdQuery,
	type GetOrganizationByIdQuery
} from "graphql/queries/get-organization-by-id.generated";
import { useUpdateOrganizationByIdMutation } from "graphql/mutations/update_organization_by_id.generated";
import { useDeleteUserFromOrganizationMutation } from "graphql/mutations/delete-user-from-organization.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useUpdatePropertyByIdMutation } from "graphql/mutations/update-property-by-id.generated";
import { UserRoleValue } from "constants/field-values";
import { canRoleRemove } from "utils/permissions-management";
import { getPropertyAddress } from "utils/helpers";
import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";

const OrganizationDetails: React.FC = () => {
	const params = useParams<{ organizationId: string }>();
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const [organizationData, setOrganizationData] = useState<GetOrganizationByIdQuery["organizations"][0] | null>(null);
	const [usersData, setUsersData] = useState<Array<Users>>([]);
	const [propertiesData, setPropertiesData] = useState<Array<Properties>>([]);
	const isCurrentUserOrganization = useMemo(() => {
		return (
			loggedUserValue.organization_id === organizationData?.id || loggedUserValue.role === UserRoleValue.admin_user
		);
	}, [loggedUserValue.organization_id, organizationData?.id]);

	const handleOnErrorGetOrganizationById = useCallback((error: ApolloError) => {
		console.log("Get Organization By Id Error: ", error);
	}, []);

	const handleOnCompleteGetOrganizationBy = useCallback((data: GetOrganizationByIdQuery) => {
		if (data.organizations.length === 0) {
			message.error("Organization not found.");
			navigate(routeNames.ADMIN_ORGANIZATIONS);
			return;
		}

		const { org_properties = [] } = data.organizations[0] || {};

		const orgUsersMap = new Map<string, Users>();

		// BUG: we grab users from the properties but is possible to have zero properties
		// and still have users in the organization
		org_properties.forEach(({ property_users }) => {
			property_users.forEach(({ user }) => {
				if (!user) return;
				orgUsersMap.set(user.id || "", user as Users);
			});
		});

		setOrganizationData(data.organizations[0]);
		setPropertiesData(org_properties as Properties[]);
		setUsersData(Array.from(orgUsersMap.values()));
	}, []);

	const { loading: loadingOrganizationById, refetch: refetchOrg } = useGetOrganizationByIdQuery({
		skip: !params.organizationId,
		variables: {
			id: params.organizationId
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetOrganizationById,
		onCompleted: handleOnCompleteGetOrganizationBy
	});

	const [updateOrganizationById, { loading: mLoading }] = useUpdateOrganizationByIdMutation({
		onCompleted: () => {
			message.success("Organization deleted successfully.");
			navigate(routeNames.ADMIN_ORGANIZATIONS);
		},
		onError: (error: ApolloError) => {
			message.error("Failed to delete organization.");
			console.log("Update Organization By Id Error: ", error);
		}
	});

	const handleOnEdit = useCallback(() => {
		if (params.organizationId) {
			const path = `${routeNames.ADMIN_ORGANIZATION_EDIT.replace(":organizationId", params.organizationId)}`;

			navigate(path);
		}
	}, [params.organizationId]);

	const handleOnAddProperties = useCallback(() => {
		navigate(routeNames.ADMIN_PROPERTY_ADD);
	}, []);

	const handleOnAddUsers = useCallback(() => {
		navigate(routeNames.ADMIN_USER_ADD);
	}, []);

	const handleOnClickProperty = useCallback(record => {
		const { id } = record;
		const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", id)}`;

		navigate(path);
	}, []);

	const handleOnClickUser = useCallback(record => {
		const { id } = record;
		const path = `${routeNames.ADMIN_USER_DETAILS.replace(":userId", id)}`;

		navigate(path);
	}, []);

	const handleDelete = useCallback(() => {
		const activeUsersCount = usersData.filter(user => user.id !== loggedUserValue.id).length;
		// If users or properties exist, show toast error
		if (activeUsersCount || propertiesData.length) {
			message.error("Cannot remove organization with existing users or properties.");
			return;
		}

		updateOrganizationById({
			variables: {
				id: params.organizationId,
				set: {
					deleted_at: new Date(),
					deleted_by: loggedUserValue.id
				}
			}
		});
	}, [usersData, propertiesData]);

	const [deleteUserFromOrganization] = useDeleteUserFromOrganizationMutation({
		onCompleted: () => {
			message.success("User removed successfully.");
			refetchOrg();
		},
		onError: (error: ApolloError) => {
			message.error("Failed to remove user.");
			console.log("Update User By Id Error: ", error);
		}
	});

	const [updateProperty] = useUpdatePropertyByIdMutation({
		onCompleted: () => {
			message.success("Property removed successfully.");
			refetchOrg();
		},
		onError: (error: ApolloError) => {
			message.error("Failed to remove property.");
			console.log("Update Property By Id Error: ", error);
		}
	});

	const UsersColumns = useMemo(
		() => [
			{
				title: "NAME",
				// dataIndex: "properties",
				render: (field, record, index) => {
					return `${record.first_name} ${record.last_name}`;
				},
				key: `NAME`
			},
			{
				title: "PHONE",
				dataIndex: "phone_number",
				key: `PHONE`
			},
			{
				title: "EMAIL",
				dataIndex: "email",
				key: `EMAIL`
			},
			{
				title: "TITLE",
				dataIndex: "title",
				key: `TITLE`
			},
			{
				title: "",
				key: "actions",
				render: (_, record: Users) => {
					const { id } = record;
					const path = `${routeNames.ADMIN_USER_EDIT.replace(":userId", id)}`;
					const isCurrentUser = loggedUserValue?.id === id;
					const canBeRemoved = canRoleRemove(loggedUserValue.role as UserRoleValue, record.role as UserRoleValue);

					const handleRemoveUserClick = () => {
						if (!canBeRemoved) {
							message.error("You do not have permission to remove this user.");
							return;
						}

						deleteUserFromOrganization({
							variables: {
								userId: id,
								orgId: params.organizationId
							}
						});
					};

					return (
						<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
							{isCurrentUserOrganization && (
								<>
									<Link
										to={{
											pathname: path
										}}
										className="inline-link"
										rel="noopener"
									>
										<img
											alt={`edit-attachment-icon`}
											src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
											className="selected-file-icon"
										/>
									</Link>
									{!isCurrentUser && (
										<RemovePopconfirm
											title="Remove User"
											description={
												<>
													<span>Are you sure you want to remove this user</span>
													<br />
													<span>from this organization?</span>
												</>
											}
											handleDeleteClick={handleRemoveUserClick}
										/>
									)}
								</>
							)}
						</div>
					);
				}
			}
		],
		[loggedUserValue]
	);

	const PropertiesColumns = useMemo(
		() => [
			{
				title: "ID",
				dataIndex: "id",
				key: `ID`
			},
			{
				title: "ADDRESS",
				key: `ADDRESS`,
				render: (record: any) => getPropertyAddress(record.address, record.standardized_address)
			},
			{
				title: "NAME",
				dataIndex: "name",
				key: `NAME`
			},
			{
				title: "STATUS",
				dataIndex: "tier_status",
				key: `STATUS`
			},
			{
				title: "",
				key: "actions",
				render: (_, record) => {
					const path = `${routeNames.ADMIN_PROPERTY_EDIT.replace(":propertyId", record.id)}`;
					const handleDeleteClick = () => {
						updateProperty({
							variables: {
								id: record.id,
								set: {
									deleted_at: new Date(),
									deleted_by: loggedUserValue.id
								}
							}
						});
					};

					return (
						<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
							{isCurrentUserOrganization && (
								<>
									<Link
										to={{
											pathname: path
										}}
										className="inline-link"
										rel="noopener"
									>
										<img
											alt={`edit-attachment-icon`}
											src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
											className="selected-file-icon"
										/>
									</Link>
									<RemovePopconfirm
										title="Delete Property"
										description="Are you sure to delete this property?"
										handleDeleteClick={handleDeleteClick}
									/>
								</>
							)}
						</div>
					);
				}
			}
		],
		[]
	);

	return (
		<div className="user-d-screen-container">
			{organizationData ? (
				<Breadcrumb
					className="products-breadcrumbs"
					items={[
						{
							title: "Organizations",
							onClick: () => {
								navigate(routeNames.ADMIN_ORGANIZATIONS);
							}
						},
						{
							title: organizationData.name
						}
					]}
				/>
			) : null}
			<div className="organization-d-container">
				{organizationData?.organizationImageURL ? (
					<img src={organizationData?.organizationImageURL} className="organization-d-photo" />
				) : (
					<img
						src={process.env.PUBLIC_URL + "/assets/images/icons/sidebar-properties.svg"}
						className="organization-d-photo placeholder"
					/>
				)}
				<div className="organization-d-info-container">
					<div className="organization-d-row">
						<span className="organization-d-name">{organizationData?.name}</span>
						<div>
							{isCurrentUserOrganization && (
								<>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className="selected-file-icon"
										onClick={handleOnEdit}
									/>
									<RemovePopconfirm
										title="Delete Organization"
										description="Are you sure to delete this organization?"
										handleDeleteClick={handleDelete}
									/>
								</>
							)}
						</div>
					</div>
					<div className="organization-d-table-row">
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"ID"}</span>
							<span className="organization-d-table-column-data">{organizationData?.id}</span>
						</div>
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"CONTACT"}</span>
							<span className="organization-d-table-column-data">{organizationData?.contact_name}</span>
						</div>
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"EMAIL"}</span>
							<span className="organization-d-table-column-data">{organizationData?.email}</span>
						</div>
					</div>
				</div>
			</div>
			<div className="organization-d-table-container">
				<div className="organization-d-row">
					<span className="organization-d-name">{"Users"}</span>
					<img
						alt={`edit-attachment-icon`}
						src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`}
						className="organization-plus-icon"
						onClick={handleOnAddUsers}
					/>
				</div>
				<Table
					dataSource={usersData}
					columns={UsersColumns}
					loading={loadingOrganizationById || mLoading}
					rowKey={record => record.id}
					onRow={(record, rowIndex) => {
						return {
							onClick: event => {
								handleOnClickUser(record);
							}
						};
					}}
				/>
			</div>
			<div className="organization-d-table-container">
				<div className="organization-d-row">
					<span className="organization-d-name">{"Properties"}</span>
					<img
						alt={`edit-attachment-icon`}
						src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`}
						className="organization-plus-icon"
						onClick={handleOnAddProperties}
					/>
				</div>
				<Table
					dataSource={propertiesData}
					columns={PropertiesColumns}
					loading={loadingOrganizationById || mLoading}
					rowKey={record => record.id}
					onRow={(record, rowIndex) => {
						return {
							onClick: event => {
								handleOnClickProperty(record);
							}
						};
					}}
				/>
			</div>
		</div>
	);
};

export default OrganizationDetails;
